import _ from "lodash";
import React, { useCallback, useEffect, useRef, useMemo } from "react";
import {
  Avatar,
  EmptyState,
  IconButton,
  Tag,
  Tooltip,
} from "@toddle-edu/ds-web";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";

import withLoader from "UIComponents/GqlWithLoader";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import Loading from "UIComponents/Loading";
import { getJobFeedQuery } from "Integrations/modules/IntegrationsQueries";

import {
  formatDate,
  getMemoizedItemsFunction,
  prepareLoadMoreFeed,
} from "Utils";
import { useHandleScroll } from "Utils/CustomHooks";

import classes from "./Step1.scss";
import { NoSearchInFilterIllustration } from "@toddle-edu/ds-theme";

import { MAP_STATUS_TO_CATEGORY } from "Integrations/modules/IntegrationsConstants";
import { WORKFLOW_CREATED_AT_FORMAT } from "Workflows/modules/Constants";
import FeedListv2 from "AppComponents/FeedListv2/FeedListv2";
import {
  CalendarScheduleOutlined,
  ChevronRightOutlined,
  RefreshArrowOutlined,
  TickCircleFilled,
  WarningCircleFilled,
} from "@toddle-edu/ds-icons";
import {
  fetchAndUpdateTitle,
  STEPS,
} from "AppComponents/ManageWorkflowsModal/modules/Utils";
import LoadingDots from "UIComponents/LoadingDots";
import { retryTemporalWorkflow } from "Workflows/modules/WorkflowsModule";

const fetchSuccessAndFailedCount = steps => {
  const groupedByType = _.groupBy(steps, "stepType");

  const statusByType = _.mapValues(groupedByType, stepsForType => {
    const statuses = _.map(stepsForType, "status");
    return statuses.includes("FAILED") ? "FAILED" : "COMPLETED";
  });

  const summaryCounts = _.countBy(_.values(statusByType));

  return {
    successStepTypes: summaryCounts.COMPLETED || 0,
    failedStepTypes: summaryCounts.FAILED || 0,
  };
};

const getStatusConfigs = ({ steps, t }) => {
  const {
    successStepTypes = 0,
    failedStepTypes = 0,
  } = fetchSuccessAndFailedCount(steps);

  if (successStepTypes === 0) {
    return {
      icon: <WarningCircleFilled size="xx-small" variant="critical" />,
      label: t("common:failed_with_count", { count: failedStepTypes }),
    };
  }

  return {
    icon: (
      <TickCircleFilled
        size="xx-small"
        variant={failedStepTypes > 0 ? "warning" : "success"}
      />
    ),
    label: t("workflows:m_of_n_successful", {
      successCount: successStepTypes,
      totalCount: successStepTypes + failedStepTypes,
    }),
  };
};

const columnList = [
  {
    locale: "workflows:updated_date",
    id: "createdAt",
    type: "CUSTOM",
    style: {
      width: "240px",
    },
  },
  {
    locale: "workflows:updated_by",
    id: "createdBy",
    type: "CUSTOM",
  },
  {
    locale: "common:updates",
    id: "updates",
    type: "CUSTOM",
  },
];

const getJobData = ({ jobs }) => {
  const data = _.map(jobs, job => {
    const {
      node: {
        id,
        createdBy,
        createdAt,
        workflowProgress = {},
        status,
        isLatest = false,
      } = {},
    } = job;

    const { steps = [], subTaskData = {} } = workflowProgress;

    const {
      id: subtaskId,
      parsedData: { retryAllowed = false, numOfRetriesAllowed = 0 } = {},
    } = subTaskData || {};

    return {
      id,
      createdBy,
      createdAt: formatDate({
        date: createdAt,
        format: WORKFLOW_CREATED_AT_FORMAT,
      }),
      updates: {
        subtaskId,
        status,
        steps,
        numOfRetriesAllowed,
        retryAllowed,
        isLatest,
      },
    };
  });

  return data;
};

const JobFeed = props => {
  const {
    jobs,
    loadMoreFeed,
    hasNextPage,
    networkStatus,
    retryTemporalWorkflow,
    jobFeedVariables,
    setHeaderProps,
    handleStepChange,
    setActiveWorkflowId,
    t,
  } = props;

  const [loadingState, setLoadingState] = React.useState({});

  const jobIdToSubtaskIdMap = useMemo(
    () =>
      _.reduce(
        jobs,
        (result, { node }) => {
          const subtaskId = _.get(
            node,
            "workflowProgress.subTaskData.id",
            null
          );
          if (node.id && subtaskId) {
            return {
              ...result,
              [node.id]: subtaskId,
            };
          }
          return result;
        },
        {}
      ),
    [jobs]
  );

  const scrollContainerRef = useRef(null);

  const getScrollRef = useCallback(() => scrollContainerRef.current, []);

  useHandleScroll({
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    getScrollRef,
    hookDependencies: [scrollContainerRef.current],
  });

  const getJobDataMemoized = getMemoizedItemsFunction({
    fn: getJobData,
    items: ["LANGUAGE", "TIMEZONE"],
  });

  const handleRetryJob = subtaskId => async e => {
    e?.stopPropagation();
    setLoadingState(prevLoadingState => {
      return {
        ...prevLoadingState,
        [subtaskId]: true,
      };
    });

    await retryTemporalWorkflow({
      variables: {
        input: {
          subTaskIds: [subtaskId],
        },
      },
      refetchQueries: [
        {
          query: getJobFeedQuery,
          variables: jobFeedVariables,
        },
      ],
    });

    setLoadingState(prevLoadingState => {
      return {
        ...prevLoadingState,
        [subtaskId]: false,
      };
    });
  };

  const getCustomFeedComponent = ({ column, columnData, t }) => {
    switch (column.id) {
      case "updates": {
        const {
          subtaskId,
          status,
          steps,
          numOfRetriesAllowed,
          retryAllowed,
          isLatest,
        } = columnData;

        const renderUpdate = () => {
          const jobStatus = MAP_STATUS_TO_CATEGORY[status] || status;

          if (jobStatus === "PROCESSING") {
            return (
              <span style={{ color: "var(--text-semantic-warning)" }}>
                {t("project:ongoing")}
              </span>
            );
          }

          const { icon: Icon, label } = getStatusConfigs({
            steps,
            t,
          });

          return (
            <span className={classes.statusButtonWrapper}>
              {Icon}
              {label}
            </span>
          );
        };

        return (
          <div className={classes.updatesContainer}>
            {renderUpdate()}
            <div className={classes.actionsContainer}>
              {isLatest && status === "FAILED" && retryAllowed ? (
                !loadingState[subtaskId] ? (
                  <Tooltip
                    tooltip={
                      numOfRetriesAllowed === 0
                        ? t("workflows:retry_limit_reached")
                        : t("common:retry")
                    }
                    placement="top"
                  >
                    <IconButton
                      type="fill"
                      dsVersion="2.0"
                      variant="neutral"
                      icon={<RefreshArrowOutlined variant="subtle" />}
                      onClick={handleRetryJob(subtaskId)}
                      disabled={numOfRetriesAllowed === 0}
                    />
                  </Tooltip>
                ) : (
                  <LoadingDots />
                )
              ) : null}

              <IconButton
                type="fill"
                dsVersion="2.0"
                variant="neutral"
                icon={<ChevronRightOutlined variant="subtle" />}
              />
            </div>
          </div>
        );
      }
      case "createdAt": {
        return (
          <div className={classes.createdAtContainer}>
            <CalendarScheduleOutlined size="xx-small" variant="subtle" />
            <span className={classes.createdAt}>{columnData}</span>
          </div>
        );
      }
      case "createdBy": {
        const { id, firstName = "", lastName = "", profileImage } = columnData;
        const name = `${firstName} ${lastName}`;
        return (
          <Tag
            dsVersion="2.0"
            size="small"
            prefix={
              <Avatar
                id={id}
                dsVersion="2.0"
                src={profileImage}
                name={name}
                shape="square"
              />
            }
          >
            <span>{name}</span>
          </Tag>
        );
      }
    }
  };

  const handleItemClick = id => {
    const activeWorkFlowSubtaskId = jobIdToSubtaskIdMap[id] || null;
    setActiveWorkflowId(activeWorkFlowSubtaskId);
    handleStepChange(STEPS.STEP_2);
    setHeaderProps(fetchAndUpdateTitle({ jobs, t, activeWorkflowId: id }));
  };

  useEffect(() => {
    if (jobs.length) {
      setHeaderProps(fetchAndUpdateTitle({ jobs, t }));
    }
  }, []);

  return (
    <>
      {!_.isEmpty(jobs) ? (
        <div className={classes.feedContainer} ref={scrollContainerRef}>
          <FeedListv2
            columnList={columnList}
            feedData={getJobDataMemoized({ jobs })}
            getCustomComponent={getCustomFeedComponent}
            onItemClick={handleItemClick}
            showBorder={true}
            headerTheme={"subtle"}
            enableStickyHeader={true}
            dsVersion="2.0"
          />
        </div>
      ) : (
        <EmptyState
          dsVersion="2.0"
          illustration={NoSearchInFilterIllustration}
          title={"No workflows found"}
        />
      )}
      {networkStatus === 3 ? <Loading /> : null}
    </>
  );
};

const mapActionCreators = {
  retryTemporalWorkflow,
};

export default compose(
  connect(null, mapActionCreators),

  graphql(getJobFeedQuery, {
    name: "getJobFeed",
    options: ({ jobFeedVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: jobFeedVariables,
    }),
    props: ({ getJobFeed, ownProps: { jobFeedVariables } }) => {
      const jobs = _.get(getJobFeed, "node", {});

      const networkStatus = getJobFeed["networkStatus"];
      const pageInfo = _.get(getJobFeed, "node.jobFeed.pageInfo", {});
      const hasNextPage = _.get(pageInfo, "hasNextPage", false);
      const endCursor = _.get(pageInfo, "endCursor", "");
      const afterV2 = endCursor ? endCursor : null;

      return {
        getJobFeed,
        jobs: _.get(jobs, "jobFeed.edges", []),
        onPusherEventTriggered: () => {
          getJobFeed.refetch();
        },
        isData: !_.isEmpty(jobs),
        isLoading: _.includes([1, 2], getJobFeed["networkStatus"]),
        isMoreJobsLoading: getJobFeed["networkStatus"] === 3,
        hasNextPage,
        networkStatus,
        loadMoreFeed: prepareLoadMoreFeed({
          variables: {
            ...jobFeedVariables,
            afterV2,
          },
          queryData: getJobFeed,
          key: "jobFeed",
        }),
      };
    },
  }),
  withPusherBind(["workflowJobCompleted"]),
  withLoader
)(JobFeed);
